import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link2, Copy, Check, Wallet, Users, MousePointerClick, Coins, BadgeCheck, Clock } from "lucide-react";

/** Profile → "Earn" panel: share link, stats, payout wallet, ledger. */
export default function AffiliatePanel() {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [wallet, setWallet] = useState("");
  const [savingWallet, setSavingWallet] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const [me, ev] = await Promise.all([api.get("/affiliate/me"), api.get("/affiliate/events")]);
    setData(me.data);
    setWallet(me.data.payout_wallet || "");
    setEvents(ev.data);
  };
  useEffect(() => { load().catch(() => toast.error("Could not load affiliate data")); }, []);

  if (!data) return <div className="panel p-6 text-xs font-mono text-[var(--text-muted)]">LOADING AFFILIATE…</div>;

  const shareUrl = `${window.location.origin}${data.share_path}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Invite link copied.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — select the link manually.");
    }
  };

  const saveWallet = async () => {
    setSavingWallet(true);
    try {
      await api.post("/affiliate/wallet", { wallet: wallet.trim() });
      toast.success("Payout wallet saved.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid wallet");
    } finally {
      setSavingWallet(false);
    }
  };

  const pct = Math.min(100, Math.round((data.pending_gbp / data.min_payout_gbp) * 100));

  return (
    <div className="panel p-6" data-testid="affiliate-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="tape-label">EARN · REFERRAL PROGRAM</span>
          <h2 className="font-display text-4xl mt-2 leading-none">GET PAID TO RECRUIT.</h2>
          <p className="text-xs font-mono text-[var(--text-muted)] mt-2 max-w-xl leading-relaxed">
            Share your link. Anyone who signs up through it is yours forever. You earn{" "}
            <span className="text-[var(--primary)]">{Math.round(data.commission_rate * 100)}%</span> of every VIP payment they ever make.
            Payouts in SOL on the last day of each month once you pass £{data.min_payout_gbp}.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Your code</div>
          <div className="font-display text-5xl text-[var(--primary)] leading-none" data-testid="affiliate-code">{data.code}</div>
        </div>
      </div>

      {/* Share link */}
      <div className="mt-6 flex gap-2">
        <div className="flex-1 flex items-center gap-2 border border-[var(--border-bright)] bg-[var(--surface)] px-3 py-2.5 font-mono text-xs text-white truncate">
          <Link2 className="w-3.5 h-3.5 text-[var(--primary)] shrink-0" />
          <span className="truncate" data-testid="affiliate-share-link">{shareUrl}</span>
        </div>
        <button onClick={copy} className="btn-brutal px-4" data-testid="affiliate-copy-button">
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)]">
        <Stat icon={MousePointerClick} label="Clicks" value={data.clicks} testid="affiliate-stat-clicks" />
        <Stat icon={Users} label="Sign-ups" value={data.referred_users} testid="affiliate-stat-referred" />
        <Stat icon={Clock} label="Pending" value={`£${data.pending_gbp.toFixed(2)}`} accent testid="affiliate-stat-pending" />
        <Stat icon={BadgeCheck} label="Paid out" value={`£${data.paid_gbp.toFixed(2)}`} testid="affiliate-stat-paid" />
      </div>

      {/* Threshold progress */}
      <div className="mt-4">
        <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1.5">
          <span>{data.eligible_for_payout ? "Eligible for next payout run" : `£${(data.min_payout_gbp - data.pending_gbp).toFixed(2)} until payout threshold`}</span>
          <span>£{data.pending_gbp.toFixed(2)} / £{data.min_payout_gbp}</span>
        </div>
        <div className="h-1.5 bg-[var(--surface)] border border-[var(--border)]">
          <div className="h-full bg-[var(--primary)] transition-[width] duration-700" style={{ width: `${pct}%` }} data-testid="affiliate-progress-bar" />
        </div>
      </div>

      {/* Wallet */}
      <div className="mt-6 pt-6 border-t border-[var(--border)]">
        <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">
          <Wallet className="w-3.5 h-3.5 text-[var(--primary)]" /> Solana payout wallet
        </label>
        <div className="flex gap-2">
          <input
            value={wallet}
            onChange={(e) => setWallet(e.target.value)}
            className="brutal flex-1"
            placeholder="Your SOL address (Phantom, Solflare…)"
            data-testid="affiliate-wallet-input"
          />
          <button onClick={saveWallet} disabled={savingWallet || wallet.trim().length < 32} className="btn-ghost text-xs" data-testid="affiliate-wallet-save">
            {savingWallet ? "Saving…" : "Save"}
          </button>
        </div>
        {!data.payout_wallet && (
          <p className="text-[10px] font-mono text-[var(--warning)] mt-2" data-testid="affiliate-wallet-warning">
            No wallet on file — you can't be paid until you add one.
          </p>
        )}
      </div>

      {/* Ledger */}
      <div className="mt-6 pt-6 border-t border-[var(--border)]">
        <span className="tape-label">COMMISSION LEDGER</span>
        <div className="mt-3 space-y-px bg-[var(--border)]">
          {events.length === 0 && (
            <div className="bg-[var(--surface)] p-4 text-center text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]" data-testid="affiliate-ledger-empty">
              No commissions yet — share your link to start earning.
            </div>
          )}
          {events.map((e) => (
            <div key={e.id} className="bg-[var(--surface)] px-4 py-2.5 flex items-center justify-between gap-3 text-xs font-mono" data-testid={`affiliate-event-${e.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <Coins className="w-3.5 h-3.5 text-[var(--primary)] shrink-0" />
                <span className="text-white truncate">@{e.buyer_username}</span>
                <span className="text-[var(--text-muted)] uppercase">{e.source} VIP · £{e.base_amount_gbp.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-[var(--primary)]">+£{e.commission_gbp.toFixed(2)}</span>
                <span className={`text-[9px] px-1.5 py-0.5 uppercase ${e.status === "paid" ? "bg-[var(--primary)] text-black" : "bg-[var(--warning)] text-black"}`}>{e.status}</span>
                <span className="text-[10px] text-[var(--text-muted)] hidden sm:inline">{new Date(e.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="bg-[var(--bg)] p-4" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
      <Icon className="w-3 h-3" /> {label}
    </div>
    <div className={`font-display text-3xl mt-1 ${accent ? "text-[var(--primary)]" : "text-white"}`}>{value}</div>
  </div>
);
